import * as React from 'react';

import Box from '@mui/material/Box';
import { useRoutes, useNavigate } from 'react-router';
import './styles/ds.css';
import './styles/screens.css';
import AppShell from './components/shell/AppShell';
import { ItemCardProvider } from './components/itemCard/ItemCardContext';
import ItemCardLayer from './components/itemCard/ItemCardLayer';
import MainGrid from './components/MainGrid';
import DashboardHome from './views/DashboardHome';
import DocumentImport from './views/DocumentImport';
import GettingStarted from './views/GettingStarted';
import VectorSpace from './views/VectorSpace';
import Examples from './views/Examples';
import NotificationsSettings from './views/NotificationsSettings';
// Lazy-loaded like the Document tab: the Assumptions register (and its MUI table /
// dialogs) is only fetched when the user navigates to it.
const Assumptions = React.lazy(() => import('./views/Assumptions'));
import {
  UserSettings, OrgSettings, RoleControl, ProjectConfig, PlatformConsole,
} from './iam/pages';

// Scrollable, centred, max-width container for admin / settings / placeholder
// pages. Fills the AppShell content slot (which is a flex row).
function Page({ children }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
      {/* Tighter gutters on phones, and extra bottom padding so the last card
          clears the fixed mobile bottom nav (~54px). */}
      <Box sx={{
        maxWidth: 1100, mx: 'auto', width: '100%',
        px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 },
        pb: { xs: '76px', sm: 2.5 },
      }}>
        {children}
      </Box>
    </Box>
  );
}

export default function Dashboard() {
  // Navigate is obtained here (always inside the Router) and handed to the item-card
  // layer via context, so the tooltip components don't import react-router-dom
  // directly (keeps them loadable in jsdom unit tests without a Router).
  const navigate = useNavigate();
  // The /account/* IAM pages render inside the AppShell, not a separate layout.
  const routeElement = useRoutes([
    { path: '/', element: <MainGrid /> },
    { path: '/analytics', element: <DashboardHome /> },
    { path: '/import-export', element: <Page><div>Import/Export</div></Page> },
    { path: '/tasks', element: <Page><div>Tasks</div></Page> },
    { path: '/document-import', element: <Page><DocumentImport /></Page> },
    { path: '/getting-started', element: <Page><GettingStarted /></Page> },
    { path: '/vector-space', element: <VectorSpace /> },
    {
      path: '/assumptions',
      element: (
        <React.Suspense fallback={<Page><div>Loading…</div></Page>}>
          <Assumptions />
        </React.Suspense>
      ),
    },
    { path: '/examples', element: <Page><Examples /></Page> },
    // #817: a deep link to one item renders the TREE PAGE, not a tree-less ItemDetails.
    // MainGrid is the same element `/` uses; useTreeState reads the id out of the path
    // (hooks/itemUrl.js) and openToItem loads and expands every ancestor, so the item
    // becomes a visible row. Prev/Next reads that visible order — on the old standalone
    // route the order was empty and both buttons were dead for anyone arriving by a
    // shared link, a notification, or the Dashboard Review CTA.
    //
    // Kept as a PATH rather than redirected into `#item=<id>`: notification alerts link
    // to `/item/<id>#comment-<id>`, and both anchor consumers match that hash with an
    // anchored regex, so an item id in the hash would break #360.
    { path: '/item/:id', element: <MainGrid /> },
    // IAM / account / admin pages (auth-gated by App's AuthGate).
    { path: '/account', element: <Page><UserSettings /></Page> },
    { path: '/account/settings', element: <Page><UserSettings /></Page> },
    { path: '/account/notifications', element: <Page><NotificationsSettings /></Page> },
    { path: '/account/org', element: <Page><OrgSettings /></Page> },
    { path: '/account/roles', element: <Page><RoleControl /></Page> },
    { path: '/account/platform', element: <Page><PlatformConsole /></Page> },
    { path: '/account/projects/:pid/config', element: <Page><ProjectConfig /></Page> },
  ]);

  return (
    <ItemCardProvider navigate={navigate}>
      <AppShell>{routeElement}</AppShell>
      <ItemCardLayer />
    </ItemCardProvider>
  );
}
